import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Alert,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  Stack,
  useTheme,
  alpha,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LoginIcon from "@mui/icons-material/Login";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DashboardLayout from "../../common/layouts/dashboard";
import { useSelector } from "react-redux";
import { user as currentUserSelector } from "../auth/auth/auth.slice";
import { Navigate } from "react-router-dom";
import adminAPI, { AdminUser } from "./admin.api";
import { aiGatewayInsightsUrl } from "./aiGatewayLink";
import UserEditTab from "./components/UserEditTab";
import UserCreditsTab from "./components/UserCreditsTab";
import UserSubscriptionsTab from "./components/UserSubscriptionsTab";
import UserRateLimitsTab from "./components/UserRateLimitsTab";
import UserAiUsageTab from "./components/UserAiUsageTab";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const currentUser = useSelector(currentUserSelector);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loginUrlDialogOpen, setLoginUrlDialogOpen] = useState(false);
  const [generatedLoginUrl, setGeneratedLoginUrl] = useState<string>("");
  const [generatingUrl, setGeneratingUrl] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Check if user is admin
  if (!currentUser?.isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getUser(userId);
      setUser(response.user);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || "Failed to fetch user");
    } finally {
      setLoading(false);
    }
  };

  const handleUserUpdated = () => {
    fetchUser();
  };

  const handleGenerateLoginUrl = async () => {
    if (!user) return;
    
    setGeneratingUrl(true);
    try {
      const response = await adminAPI.generateLoginUrl(user._id);
      // Replace localhost:3000 with current domain if needed
      let loginUrl = response.loginUrl;
      if (loginUrl.includes('localhost:3000') && window.location.hostname !== 'localhost') {
        const currentOrigin = window.location.origin;
        loginUrl = loginUrl.replace('http://localhost:3000', currentOrigin);
      }
      setGeneratedLoginUrl(loginUrl);
      setLoginUrlDialogOpen(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setSnackbarMessage(`Failed to generate login URL: ${error.response?.data?.error?.message || error.message}`);
      setSnackbarOpen(true);
    } finally {
      setGeneratingUrl(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(generatedLoginUrl);
      setSnackbarMessage("Login URL copied to clipboard!");
      setSnackbarOpen(true);
      // Close the dialog after successful copy
      handleCloseDialog();
    } catch (err: unknown) {
      setSnackbarMessage("Failed to copy URL");
      setSnackbarOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setLoginUrlDialogOpen(false);
    setGeneratedLoginUrl("");
  };

  if (loading) {
    return (
      <DashboardLayout active="admin" name="Admin Panel">
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </DashboardLayout>
    );
  }

  if (error || !user) {
    return (
      <DashboardLayout active="admin" name="Admin Panel">
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "User not found"}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin")}
            variant="outlined"
          >
            Back to Admin Panel
          </Button>
        </Container>
      </DashboardLayout>
    );
  }

  const theme = useTheme();

  return (
    <DashboardLayout active="admin" name="Admin Panel">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/admin")}
              variant="text"
              sx={{
                mb: 2,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              Back to Users
            </Button>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                  User Management
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    {user.email}
                  </Typography>
                  {user.isAdmin && (
                    <Chip 
                      label="Admin" 
                      color="primary" 
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {user._id}
                  </Typography>
                  <Chip 
                    label={user.status}
                    color={user.status === 'active' ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>
              
              {/* Action Buttons */}
              <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                component="a"
                href={aiGatewayInsightsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon />}
                size="large"
                sx={{ textTransform: 'none' }}
              >
                AI Gateway Usage
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerateLoginUrl}
                disabled={generatingUrl}
                startIcon={generatingUrl ? <CircularProgress size={20} /> : <LoginIcon />}
                size="large"
                sx={{
                  textTransform: 'none',
                  background: generatingUrl ? undefined : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '&:hover': {
                    background: generatingUrl ? undefined : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                {generatingUrl ? 'Generating...' : 'Generate One-Time Login'}
              </Button>
              </Stack>
            </Box>
          </Box>

          {/* Main Content */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                px: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                }
              }}
            >
              <Tab label="User Details" />
              <Tab label="Credits" />
              <Tab label="Subscriptions" />
              <Tab label="Rate Limits" />
              <Tab label="AI Usage" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              <TabPanel value={tabValue} index={0}>
                <UserEditTab user={user} onSave={handleUserUpdated} onError={setError} />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <UserCreditsTab user={user} />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <UserSubscriptionsTab user={user} />
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <UserRateLimitsTab user={user} />
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <UserAiUsageTab user={user} />
              </TabPanel>
            </Box>
          </Paper>
        </Stack>

        {/* Login URL Dialog */}
        <Dialog
          open={loginUrlDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">Login URL Generated</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Important:</strong> Open this link in an incognito/private browser tab to avoid being logged out of your admin session.
            </Alert>
            <Alert severity="info" sx={{ mb: 2 }}>
              This one-time login link will expire in <strong>1 hour</strong> and can only be used once.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              User: <strong>{user?.email}</strong>
            </Typography>
            <TextField
              fullWidth
              value={generatedLoginUrl}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                endAdornment: (
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={handleCopyUrl} edge="end" aria-label="Copy login URL to clipboard">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50',
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Container>
    </DashboardLayout>
  );
}

export default UserDetail;